import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { getCookie, setCookie, getCookieOpts } from '../lib/cookies';

const UserContext = createContext();

// Signing out (or starting a new sign-in) wipes the browser's storage: the
// session, RingCentral tokens, a client's questionnaire draft. Only these
// display preferences survive (Nic 2026-10-09: people lost their kanban
// columns and board themes every time). Anything not listed is wiped, so a
// new key is private by default.
const KEEP_ON_SIGN_OUT = [
  'view_v2', // kanban columns (also saved to the server, migration 210)
  'view_v2:known',
  'coTheme',
  'co.readyForCm.slim',
  'osBoardTheme',
  'os-meetings-view',
  'os.signingUp.wonSlim',
  'onboarding-schedule-rail',
  'mo_auto_investigate',
  // CM Assignment's Caseloads order and measure (2026-10-13).
  'cmAssignment.caseloadSort',
  'cmAssignment.caseloadMeasure',
];
const clearLocalStorageKeepingPrefs = () => {
  try {
    const kept = KEEP_ON_SIGN_OUT.map((k) => [k, window.localStorage.getItem(k)]).filter(([, v]) => v !== null);
    window.localStorage.clear();
    kept.forEach(([k, v]) => window.localStorage.setItem(k, v));
  } catch {
    // Storage blocked: nothing to clear or keep.
  }
};

/**
 * Permission checking utilities
 */
const checkPermission = (user, permissionKey) => {
  if (!user?._permissions) return false;
  return user._permissions.includes(permissionKey);
};

const checkAnyPermission = (user, permissionKeys) => {
  if (!user?._permissions || !permissionKeys?.length) return false;
  return permissionKeys.some((key) => user._permissions.includes(key));
};

const checkAllPermissions = (user, permissionKeys) => {
  if (!user?._permissions || !permissionKeys?.length) return false;
  return permissionKeys.every((key) => user._permissions.includes(key));
};

const checkRole = (user, roleName) => {
  if (!user?._roles) return false;
  return user._roles.includes(roleName);
};

const checkAnyRole = (user, roleNames) => {
  if (!user?._roles || !roleNames?.length) return false;
  return roleNames.some((name) => user._roles.includes(name));
};

const DEFUALTS = {
  user: null,
  token: null,
  verifyId: null,
};

const getJsonItem = (key) => {
  const val = window.localStorage.getItem(key);
  if (!val) {
    return null;
  }
  return JSON.parse(val);
};

// The signed-in user is kept in localStorage, so it must never carry a full
// SSN (Nic 2026-09-28). The server now sends only userSsnLast4; this also
// scrubs copies saved before that change.
const withoutSsn = (user) => {
  if (!user || !('userSsn' in user)) return user;
  const { userSsn, ...rest } = user;
  return rest;
};

const getStoredUser = () => {
  const stored = getJsonItem('user');
  if (stored && 'userSsn' in stored) {
    const clean = withoutSsn(stored);
    try {
      window.localStorage.setItem('user', JSON.stringify(clean));
    } catch (_) {
      // Storage unavailable: the in-memory copy is still clean.
    }
    return clean;
  }
  return stored;
};

const UserProvider = ({ children }) => {
  // The next person must never see the last one's cached data.
  const queryClient = useQueryClient();
  const [context, _setContext] = useState({
    user: getStoredUser(),
    token: getCookie('token') || null,
    verifyId: window.sessionStorage.getItem('verifyId') || null,
  });

  const updateUser = (userData, callback = () => {}) => {
    const updates = {
      ...context,
      user: withoutSsn({ ...context.user, ...userData }),
    };

    window.localStorage.setItem('user', JSON.stringify(updates.user));

    _setContext(updates);
    callback();
  };

  const setVerifyId = (id) => {
    clearLocalStorageKeepingPrefs();
    queryClient.clear();
    window.sessionStorage.setItem('verifyId', id);
    _setContext({ ...DEFUALTS, verifyId: id });
  };

  const signIn = (userData, callback = () => {}) => {
    const { _token, ...rawData } = userData;
    const data = withoutSsn(rawData);
    const opts = getCookieOpts();

    window.sessionStorage.clear();
    queryClient.clear();
    window.localStorage.setItem('user', JSON.stringify(data));
    setCookie('token', _token, opts);

    _setContext({
      user: data,
      token: _token,
      verifyId: null,
    });

    callback();
  };

  const signOut = (callback = () => {}) => {
    const opts = getCookieOpts();
    opts.maxAge = 0;

    clearLocalStorageKeepingPrefs();
    window.sessionStorage.clear();
    queryClient.clear();

    setCookie('token', null, opts);

    // Marker for PrivateRoute: the logged-out re-render this triggers is a
    // sign-out bounce, NOT a deep link - do not stash the current page as a
    // post-login destination (prevents the 401 error/logout loop and gives
    // explicit sign-out a clean slate). Set synchronously BEFORE _setContext
    // so it exists when PrivateRoute re-renders; sessionStorage.clear()
    // above also wiped any pending stash.
    window.sessionStorage.setItem('signedOutAt', String(Date.now()));

    _setContext({ ...DEFUALTS });
    // callback is optional — guard it so wiring like `onClick={signOut}` (which
    // passes the click event as the first arg) doesn't throw "callback is not a
    // function" / "e is not a function".
    if (typeof callback === 'function') callback();
  };

  // Permission checking functions bound to current user
  const hasPermission = useCallback(
    (permissionKey) => checkPermission(context.user, permissionKey),
    [context.user],
  );

  const hasAnyPermission = useCallback(
    (permissionKeys) => checkAnyPermission(context.user, permissionKeys),
    [context.user],
  );

  const hasAllPermissions = useCallback(
    (permissionKeys) => checkAllPermissions(context.user, permissionKeys),
    [context.user],
  );

  const hasRole = useCallback(
    (roleName) => checkRole(context.user, roleName),
    [context.user],
  );

  const hasAnyRole = useCallback(
    (roleNames) => checkAnyRole(context.user, roleNames),
    [context.user],
  );

  // Convenience getters for roles and permissions arrays
  const roles = useMemo(() => context.user?._roles || [], [context.user]);
  const permissions = useMemo(() => context.user?._permissions || [], [context.user]);

  // Track last refresh to debounce rapid navigation
  const lastRefreshRef = useRef(0);
  const REFRESH_DEBOUNCE_MS = 1000; // Minimum 1 second between refreshes

  /**
   * Refresh permissions from the server
   * Called on navigation to ensure permissions are up to date
   */
  const refreshPermissions = useCallback(async () => {
    // Don't refresh if not logged in
    if (!context.token || !context.user) return;

    // Debounce rapid refreshes
    const now = Date.now();
    if (now - lastRefreshRef.current < REFRESH_DEBOUNCE_MS) return;
    lastRefreshRef.current = now;

    try {
      const res = await axios.get('/auth/permissions');
      const { permissions: newPermissions, roles: newRoles } = res.data || {};

      // Guard against unexpected response format
      if (!Array.isArray(newPermissions) || !Array.isArray(newRoles)) {
        console.debug('refreshPermissions: unexpected response format', res.data);
        return;
      }

      // Only update if permissions actually changed
      const currentPerms = context.user?._permissions || [];
      const currentRoles = context.user?._roles || [];

      const permsChanged =
        newPermissions.length !== currentPerms.length ||
        newPermissions.some((p) => !currentPerms.includes(p));
      const rolesChanged =
        newRoles.length !== currentRoles.length ||
        newRoles.some((r) => !currentRoles.includes(r));

      if (permsChanged || rolesChanged) {
        const updatedUser = {
          ...context.user,
          _permissions: newPermissions,
          _roles: newRoles,
        };

        window.localStorage.setItem('user', JSON.stringify(updatedUser));
        _setContext((prev) => ({
          ...prev,
          user: updatedUser,
        }));
      }
    } catch (err) {
      // Silently fail - user keeps existing permissions
      // This could happen if token expired, which will be handled by other auth logic
      console.debug('Failed to refresh permissions:', err.message);
    }
  }, [context.token, context.user]);

  const value = {
    user: context.user,
    token: context.token,
    verifyId: context.verifyId,
    updateUser,
    signIn,
    signOut,
    setVerifyId,
    // Permission and role checking
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    roles,
    permissions,
    // Permission refresh
    refreshPermissions,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export { UserProvider, useUser };
